import React, { useEffect, useMemo, useState } from 'react';
import type { ComponentProps } from 'react';

import {
  universalReferenceActions,
  RefValueItemInterface,
} from '../../../universal-reference/store/universalReferenceStore';
import { SelectInput } from '../forms';

/**
 * Собственные пропсы RefValueSelector (то, что НЕ наследуется из SelectInput).
 *
 * `dataTitle`/`dataValue`/`sort`/`isSearchable`/`name`/`label`/`placeholder`/`disabled`/`required`/
 * `error`/... наследуются из {@link SelectInputProps} через `ComponentProps<typeof SelectInput>` и
 * свободно протекают вниз через `...rest` → `<SelectInput>`. Здесь остаются только поля, которые
 * селектор достаивает из `{...props}` и обрабатывает сам: идентификатор справочника и фильтры.
 */
export interface RefValueSelectorOwnProps {
  /**
   * Идентификатор справочника (universal-reference), из которого грузятся значения.
   * Строка или число — совместимость с legacy-вызовами (`refId='27'`, `refId={ 52 }`).
   */
  refId: number | string;
  /** Фильтр значений по полю int1 (побайтовое сравнение через String(...)). */
  int1?: number | string;
  // фильтр по определённым ключам, которые нужно показать
  /** Показывать только опции с id из этого набора. */
  filterKey?: unknown[];
  // фильтр по ключам, которые надо исключить
  /** Исключить опции с id из этого набора. */
  excludeKey?: unknown[];
  /** Сортировать на стороне БД (по полю sort) вместо клиентской сортировки SelectInput'а. */
  sortFromBD?: boolean;
}

/**
 * Пропсы RefValueSelector для внешнего потребителя (и для field-обёрток через
 * `ComponentProps<typeof RefValueSelector>`). Полный поток пропсов утекает в `<SelectInput>`,
 * поэтому наследуемся от Formik-обёртки одиночного селекта.
 */
export type RefValueSelectorProps = ComponentProps<typeof SelectInput> & RefValueSelectorOwnProps;

const RefValueSelector = React.memo((props: RefValueSelectorProps) => {
  const {
    dataTitle = 'name',
    dataValue = 'id',
    refId,
    int1,
    filterKey,
    excludeKey,
    sortFromBD = false,
    ...rest
  } = props;

  const [loading, setLoading] = useState(false);
  // Элементы универсального справочника — полиморфная форма ({ id, string1, int1, sort, ... }),
  // нормализуется ниже в filteredValues. any[] сознательно: разные справочники несут разные поля,
  // а сужать под каждый refId без generic'а нельзя. Совпадает с поведением прежнего .jsx.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [values, setValues] = useState<any[]>([]);

  const filteredValues = useMemo(() => {
    let result = [9, 66, 73].includes(+refId)
      ? values.map(v => ({ ...v, color: v.string1 }))
      : values;

    if (int1) {
      result = result.filter(v => String(v.int1) === String(int1));
    }

    if (filterKey?.length) {
      const filterSet = new Set(filterKey);
      result = result.filter(v => filterSet.has(v.id));
    }

    if (excludeKey?.length) {
      const excludeSet = new Set(excludeKey);
      result = result.filter(v => !excludeSet.has(v.id));
    }

    if (sortFromBD) {
      result = [...result].sort((a, b) => ((a.sort as number) ?? 0) - ((b.sort as number) ?? 0));
    }

    return result;
  }, [values, refId, int1, filterKey, excludeKey, sortFromBD]);

  useEffect(() => {
    const abortController = new AbortController();

    setLoading(true);
    // экшен стора кэширует значения по refId на сессию — повторные mount'ы идут без запроса
    universalReferenceActions.getRefValues(refId)
      .then((refValues: RefValueItemInterface[]) => {
        if (!abortController.signal.aborted) {
          setValues(refValues);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => abortController.abort();
  }, [refId]);

  return (
    <SelectInput
      { ...rest }
      data={ filteredValues }
      dataTitle={ dataTitle }
      dataValue={ dataValue }
      loading={ loading }
      sort={ !sortFromBD }
    />
  );
});

export default RefValueSelector;
